var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Modal from 'react-modal';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { Header, Content, Footer, IconWrapper } from './styles';
import SwipeDownIcon from '../icons/swipe-icon';
import { BOTTOM_SHEET_DEFAULT_STYLES, BOTTOM_SHEET_DEFAULT_CONFIG } from './constants';
var BottomSheet = function (_a) {
    var isOpen = _a.isOpen, header = _a.header, content = _a.content, footer = _a.footer, onClose = _a.onClose, _b = _a.showSwipeIcon, showSwipeIcon = _b === void 0 ? true : _b, contentRef = _a.contentRef, rootElementSelector = _a.rootElementSelector, _c = _a.borderRadius, borderRadius = _c === void 0 ? BOTTOM_SHEET_DEFAULT_CONFIG.borderRadius : _c, _d = _a.maxHeightRatio, maxHeightRatio = _d === void 0 ? BOTTOM_SHEET_DEFAULT_CONFIG.maxHeightRatio : _d, _e = _a.closeThreshold, closeThreshold = _e === void 0 ? BOTTOM_SHEET_DEFAULT_CONFIG.closeThreshold : _e, _f = _a.closeVelocityThreshold, closeVelocityThreshold = _f === void 0 ? BOTTOM_SHEET_DEFAULT_CONFIG.closeVelocityThreshold : _f, _g = _a.enableSwipeToClose, enableSwipeToClose = _g === void 0 ? true : _g;
    var _h = useState(0), maxHeight = _h[0], setMaxHeight = _h[1];
    var _j = useState(false), isMounted = _j[0], setIsMounted = _j[1];
    // Initialize modal and calculate max height
    useEffect(function () {
        Modal.setAppElement(rootElementSelector ? rootElementSelector : '#root');
        setIsMounted(true);
        setMaxHeight(typeof window !== 'undefined' ? window.innerHeight * maxHeightRatio : 0);
    }, [rootElementSelector, maxHeightRatio]);
    var _k = useSpring(function () { return ({
        y: typeof window !== 'undefined' ? window.innerHeight : 0,
        config: { tension: BOTTOM_SHEET_DEFAULT_CONFIG.tension, friction: BOTTOM_SHEET_DEFAULT_CONFIG.friction },
    }); }), y = _k[0].y, api = _k[1];
    // Handle open/close animations
    useEffect(function () {
        if (!isMounted) {
            return;
        }
        api.start({ y: isOpen ? 0 : typeof window !== 'undefined' ? window.innerHeight : 0 });
    }, [isOpen, api, isMounted]);
    var onDrag = useCallback(function (state) {
        if (!enableSwipeToClose) {
            return;
        }
        var last = state.last, movement = state.movement, velocity = state.velocity;
        var my = movement[1];
        var vy = velocity[1];
        var newY = Math.max(my, 0);
        api.start({ y: newY });
        if (last) {
            var shouldClose = my > closeThreshold || vy > closeVelocityThreshold;
            if (shouldClose) {
                api.start({
                    y: typeof window !== 'undefined' ? window.innerHeight : 0,
                    config: { tension: 200, friction: 25 },
                });
                setTimeout(onClose, 500);
            }
            else {
                api.start({ y: 0 });
            }
        }
    }, [api, closeThreshold, closeVelocityThreshold, onClose, enableSwipeToClose]);
    // Drag gesture handler with proper type annotation
    var bind = useDrag(onDrag, {
        from: function () { return [0, y.get()]; },
        axis: 'y',
        bounds: { top: 0 },
    });
    // Memoize animated div styles to avoid recalculating on every render
    var animatedDivStyles = useMemo(function () { return ({
        y: y,
        maxHeight: maxHeight,
        width: '100%',
        backgroundColor: 'white',
        borderTopLeftRadius: borderRadius,
        borderTopRightRadius: borderRadius,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    }); }, [y, maxHeight, borderRadius]);
    if (!isMounted) {
        return null;
    }
    return (React.createElement(Modal, { isOpen: isOpen, onRequestClose: onClose, style: BOTTOM_SHEET_DEFAULT_STYLES, closeTimeoutMS: 300, contentLabel: "Bottom Sheet Modal" },
        React.createElement(animated.div, __assign({ style: animatedDivStyles }, (enableSwipeToClose ? bind() : {})),
            showSwipeIcon && (React.createElement(IconWrapper, null,
                React.createElement(SwipeDownIcon, null))),
            React.createElement(Header, null, header),
            React.createElement(Content, { ref: contentRef }, content),
            footer && React.createElement(Footer, null, footer))));
};
export default React.memo(BottomSheet);
